import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import FeatherIcon from "feather-icons-react";
import debounce from "lodash.debounce";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import useAxios from "../../utils/useAxios";
import { CustomModal } from "../../components/CustomModel/CustomModal";
import PhotoLightbox from "../../components/PhotoLightbox";
import SelectInput from "../../components/SelectInput";
import TournamentShareModal from "../tournaments/export/TournamentShareModal";
import { getInitials, COUNTRY_OPTIONS, LEVEL_OPTIONS } from "../../constants";
import { getOptionName } from "../../utils/getOptionName";
import { useTheme } from "../../contexts/ThemeContext";
import { CompactTableRow } from "../../components/CompactTableRow";
import { parseDate } from "../../utils/parseDate";

// Same reasoning as ClubProfileModal.js's own copy of this - a same-day
// 18:00-21:00 tournament isn't "live" all day just because it's the right
// calendar day.
const getTournamentStatus = (tournament) => {
    if (!tournament.start_date || !tournament.end_date) return "upcoming";
    const now = new Date();
    const start = parseDate(tournament.start_date);
    if (!start) return "upcoming";
    const end = parseDate(tournament.end_date);
    if (!end) return "upcoming";
    if (now < start) return "upcoming";
    if (now > end) return "completed";
    return "live";
};

const isSameCalendarDay = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const getStatusClass = (tournament) => {
    const status = getTournamentStatus(tournament);
    if (status === "live") return "in-progress";
    if (status === "completed") return "completed";
    const start = parseDate(tournament.start_date);
    if (start && isSameCalendarDay(start, new Date())) return "today";
    return "expected";
};

const formatYMD = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const formatTitle = (text) => {
    if (!text) return "";
    return text.toLowerCase().split(" ").map(word => word.split("-").map(part => part.charAt(0).toUpperCase() + part.slice(1)).join("-")).join(" ");
};

const tournamentsCountLabel = (count, t) => {
    const mod10 = count % 10;
    const mod100 = count % 100;
    let key = "tournament_many";
    if (mod10 === 1 && mod100 !== 11) key = "tournament_one";
    else if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) key = "tournament_few";
    return `${count} ${t(key)}`;
};

const locationCache = {};
const locationTournamentsCache = {};
const locationPlayersCache = {};

// Mirrors ClubProfileModal.js's own ClubCalendarTab, filtered by `location`
// instead of `club` - see backend-tasks/2026-10-17-location-profile-endpoints.md,
// the search endpoint doesn't support this filter yet.
const LocationCalendarTab = ({ locationId, searchInputRef }) => {
    const { t, i18n } = useTranslation();
    const api = useAxios();
    const history = useHistory();
    const { compactTable } = useTheme();
    const [year, setYear] = useState(() => new Date().getFullYear());
    const cacheKey = `${locationId}_${year}`;
    const [tournaments, setTournaments] = useState(() => locationTournamentsCache[cacheKey] || []);
    const [currentPage, setCurrentPage] = useState("");
    const [loader, setLoader] = useState(false);
    const [skeletonCards, setSkeletonCards] = useState(() => (locationTournamentsCache[cacheKey]?.length > 0 ? 0 : 5));
    const [searchQuery, setSearchQuery] = useState("");
    const { ref, inView } = useInView({ threshold: 0, rootMargin: '200px' });
    const requestIdRef = useRef(0);

    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const options = [];
        for (let y = currentYear + 2; y >= currentYear - 6; y--) options.push({ id: y, name: String(y) });
        return options;
    }, []);

    useEffect(() => {
        requestIdRef.current += 1;
        const firstDay = new Date(year, 0, 1);
        const lastDay = new Date(year, 11, 31);
        const params = new URLSearchParams();
        params.set("location", locationId);
        params.set("all", "true");
        params.set("start_date", formatYMD(firstDay));
        params.set("end_date", formatYMD(lastDay));

        const cached = locationTournamentsCache[`${locationId}_${year}`];
        if (cached && cached.length > 0) {
            setTournaments(cached);
            setSkeletonCards(0);
        } else {
            setTournaments([]);
            setSkeletonCards(5);
        }
        setCurrentPage(`/api/v1.0/tournaments/search/?${params.toString()}`);
    }, [locationId, year]);

    const getTournaments = async () => {
        const requestId = requestIdRef.current;
        setLoader(true);
        try {
            const response = await api.get(currentPage);
            if (requestId !== requestIdRef.current) return;

            const results = response.data.results || [];
            const totalCount = response.data.count || 0;

            setTournaments(prev => {
                const existingIds = new Set(prev.map(t => t.id));
                const newItems = results.filter(t => !existingIds.has(t.id));
                const updated = [...prev, ...newItems];
                locationTournamentsCache[`${locationId}_${year}`] = updated;
                return updated;
            });
            setCurrentPage(response.data.next ? response.data.next.replace(/^https?:\/\/[^/]+/, "") : null);

            const updatedCount = tournaments.length + results.length;
            const remaining = totalCount - updatedCount;
            setSkeletonCards(remaining > 0 ? Math.min(5, remaining) : 0);
        } catch (error) {
            if (requestId !== requestIdRef.current) return;
            console.error(error);
        } finally {
            if (requestId === requestIdRef.current) setLoader(false);
        }
    };

    useEffect(() => {
        if ((inView || tournaments.length === 0) && currentPage && !loader) {
            getTournaments();
        }
    }, [inView, currentPage, loader]);

    const currentLang = (typeof i18n !== "undefined" && i18n.language) ? i18n.language : "uk";

    const monthGroups = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        const sorted = [...tournaments]
            .filter(tournament => tournament.start_date)
            .filter(tournament => !query || tournament.name?.toLowerCase().includes(query))
            .sort((a, b) => {
                const dateA = parseDate(a.start_date) || 0;
                const dateB = parseDate(b.start_date) || 0;
                return dateB - dateA;
            });

        const groups = [];
        sorted.forEach(tournament => {
            const d = parseDate(tournament.start_date);
            if (!d) return;
            const key = `${d.getFullYear()}-${d.getMonth()}`;
            const last = groups[groups.length - 1];
            if (last && last.key === key) {
                last.items.push(tournament);
                return;
            }
            groups.push({ key, label: formatTitle(d.toLocaleString(currentLang, { month: "long" })), items: [tournament] });
        });
        return groups;
    }, [tournaments, currentLang, searchQuery]);

    let compactRowIndex = 0;

    return (
        <div>
            <div style={{ margin: "10px 0" }}>
                <div style={{ width: "140px" }}>
                    <SelectInput
                        data={yearOptions}
                        default_text={String(year)}
                        default_value={year}
                        setInputValue={setYear}
                        input_name="year_select"
                        show_label={false}
                        background={true}
                    />
                </div>
                <div className="form-group" style={{ position: "relative", marginTop: "10px" }}>
                    <FeatherIcon icon="search" size={16} className="search-icon" />
                    <input
                        ref={searchInputRef}
                        type="text"
                        placeholder={t("Search tournament by name")}
                        style={{ paddingLeft: "30px" }}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="search"
                    />
                    {searchQuery && (
                        <FeatherIcon
                            icon="x"
                            size={16}
                            onClick={() => setSearchQuery("")}
                            style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", color: "#888", cursor: "pointer" }}
                        />
                    )}
                </div>
            </div>

            {!loader && tournaments.length === 0 && currentPage === null ? (
                <p style={{ padding: "15px", textAlign: "center", color: "var(--secondary-color)" }}>{t("No tournaments this year")}</p>
            ) : !loader && monthGroups.length === 0 && currentPage === null ? (
                <p style={{ padding: "15px", textAlign: "center", color: "var(--secondary-color)" }}>{t("No tournaments found")}</p>
            ) : compactTable ? (
                <div className="trows">
                    {monthGroups.map(group => (
                        <React.Fragment key={group.key}>
                            <div className="week-divider">
                                <span className="week-divider-range">{group.label} {year}</span>
                                <span className="week-divider-count">{tournamentsCountLabel(group.items.length, t)}</span>
                            </div>
                            {group.items.map(tournament => {
                                const playersValue = tournament.players != null
                                    ? `${tournament.players}${tournament.player_quantity != null ? `/${tournament.player_quantity}` : ""}`
                                    : "—";
                                const zebra = compactRowIndex++ % 2 === 1;
                                return (
                                    <CompactTableRow
                                        key={tournament.id}
                                        variant="tournaments"
                                        zebra={zebra}
                                        statusDotClass={getStatusClass(tournament)}
                                        avatarSrc={tournament.club_logo || tournament.thumbnail || tournament.image}
                                        title={formatTitle(tournament.name)}
                                        onClick={() => history.push(`/tournaments/${tournament.id}`)}
                                        stats={[
                                            { icon: "calendar", value: tournament.date_range || "—" },
                                            { icon: "award", value: tournament.club || "—", className: "stat-location" },
                                            { icon: "users", value: playersValue }
                                        ]}
                                    />
                                );
                            })}
                        </React.Fragment>
                    ))}
                    {(loader || (currentPage && tournaments.length === 0)) && skeletonCards > 0 && Array.from({ length: skeletonCards }).map((_, idx) => (
                        <div className="trow trow-tournaments" key={`skel-trow-${idx}`} style={{ pointerEvents: "none" }}>
                            <span />
                            <Skeleton circle width={20} height={20} />
                            <Skeleton width="60%" height={12} />
                            <Skeleton width="90%" height={11} />
                            <Skeleton width="90%" height={11} />
                            <Skeleton width="90%" height={11} />
                            <span />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="tournament-cards">
                    {monthGroups.map(group => (
                        <React.Fragment key={group.key}>
                            <div className="week-divider">
                                <span className="week-divider-range">{group.label} {year}</span>
                                <span className="week-divider-count">{tournamentsCountLabel(group.items.length, t)}</span>
                            </div>
                            {group.items.map(tournament => {
                                const status = getTournamentStatus(tournament);
                                const statusClass = status === "live" ? "in-progress" : status === "completed" ? "completed" : "expected";
                                const statusIcon = statusClass === "in-progress" ? "activity" : statusClass === "completed" ? "check" : "clock";
                                const statusLabel = status === "live" ? t("In progress") : status === "completed" ? t("Completed") : t("Expected");
                                return (
                                    <div
                                        className="tournament-card"
                                        key={tournament.id}
                                        onClick={() => history.push(`/tournaments/${tournament.id}`)}
                                    >
                                        <div className={`tournament-card-date ${getStatusClass(tournament)}`}>{tournament.date_range}</div>
                                        <div className="tournament-card-top">
                                            <div className="tournament-card-thumb">
                                                {(() => {
                                                    const thumbSrc = tournament.club_logo || tournament.thumbnail || tournament.image;
                                                    if (thumbSrc) return <img src={thumbSrc} alt="" />;
                                                    const label = tournament.club || tournament.name;
                                                    return label
                                                        ? <span style={{ fontSize: "16px", fontWeight: 600 }}>{getInitials(label)}</span>
                                                        : <FeatherIcon icon="camera-off" />;
                                                })()}
                                            </div>
                                            <div className="tournament-card-titles">
                                                <div className="tournament-card-title">{formatTitle(tournament.name)}</div>
                                                <div className="tournament-card-club">{tournament.club}</div>
                                                <div className="tournament-card-info">
                                                    {tournament.players != null && (
                                                        <span className="tournament-card-players">
                                                            <FeatherIcon icon="users" size={11} />{tournament.players}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <details className="tournament-card-details">
                                            <summary className="tournament-card-toggle" onClick={e => e.stopPropagation()}>
                                                <svg className="tournament-card-toggle-arrow" height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                                                    <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
                                                </svg>
                                            </summary>
                                            <div className="tournament-card-meta">
                                                <table className="tournament_table">
                                                    <tbody>
                                                    <tr>
                                                        <td>{t("Status")}</td>
                                                        <td>
                                                            <span className={`t-status-badge-sm inline ${statusClass}`}>
                                                                <FeatherIcon icon={statusIcon} />{statusLabel}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr><td>{t("Date")}</td><td>{tournament.days?.length > 0 ? tournament.days.map(day => t(`days_short.${day}`)).join("-") + " · " : ""}{tournament.date_range}</td></tr>
                                                    <tr><td>{t("Club")}</td><td>{tournament.club}</td></tr>
                                                    <tr><td>{t("Format")}</td><td>{tournament.gender && ((tournament.gender === 3 && tournament.grade === 1) ? t("Admixed") : t(`gender.${tournament.gender}`))}</td></tr>
                                                    <tr><td>{t("Players")}</td><td style={{ textAlign: "right" }}><div style={{ position: "relative", height: "30px" }}><div className="tournament-players" style={{ color: "var(--primary-color) !important", position: "absolute", right: 0, top: "-5px" }}>{tournament.players}</div></div></td></tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </details>
                                    </div>
                                );
                            })}
                        </React.Fragment>
                    ))}
                    {(loader || (currentPage && tournaments.length === 0)) && skeletonCards > 0 && Array.from({ length: skeletonCards }).map((_, idx) => (
                        <div className="tournament-card" key={`skel-${idx}`} style={{ pointerEvents: 'none' }}>
                            <div className="tournament-card-date" style={{ border: 'none', background: 'transparent', padding: 0 }}>
                                <Skeleton width={56} height={16} borderRadius={20} />
                            </div>
                            <div className="tournament-card-top">
                                <div className="tournament-card-thumb" style={{ background: 'transparent' }}>
                                    <Skeleton circle width={52} height={52} />
                                </div>
                                <div className="tournament-card-titles" style={{ flex: 1, minWidth: 0 }}>
                                    <div className="tournament-card-title"><Skeleton width="65%" height={16} /></div>
                                    <div className="tournament-card-club"><Skeleton width="40%" height={13} style={{ marginTop: 4 }} /></div>
                                    <div className="tournament-card-info"><Skeleton width="50%" height={11} style={{ marginTop: 4 }} /></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <div ref={ref} style={{ height: "20px", width: "100%" }}></div>
        </div>
    );
};

// Mirrors ClubProfileModal.js's own ClubPlayersTab - same indirect-derivation
// problem (no direct player<->location link), see
// backend-tasks/2026-10-17-location-profile-endpoints.md.
const LocationPlayersTab = ({ locationId, searchInputRef }) => {
    const { t } = useTranslation();
    const api = useAxios();
    const history = useHistory();
    const routerLocation = useLocation();
    const { compactTable } = useTheme();
    const [players, setPlayers] = useState(() => locationPlayersCache[locationId]?.players || []);
    const [currentPage, setCurrentPage] = useState("");
    const [loader, setLoader] = useState(false);
    const [skeletonCards, setSkeletonCards] = useState(() => (locationPlayersCache[locationId]?.players?.length > 0 ? 0 : 5));
    const [failed, setFailed] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [query, setQuery] = useState("");
    const { ref, inView } = useInView({ threshold: 0, rootMargin: '200px' });
    const requestIdRef = useRef(0);

    const debouncedSetQuery = useMemo(() => debounce(setQuery, 400), []);
    useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery]);

    const compactPageSize = () => Math.max(10, Math.ceil(window.innerHeight / 40) + 5);

    useEffect(() => {
        requestIdRef.current += 1;
        const params = new URLSearchParams();
        if (query.trim().length >= 2) params.set("q", query.trim());
        if (compactTable) params.set("limit", String(compactPageSize()));

        const cached = query.trim().length >= 2 ? null : locationPlayersCache[locationId];
        setFailed(false);
        const search = params.toString();
        const firstPage = `/api/v1.0/locations/${locationId}/players/${search ? `?${search}` : ""}`;
        if (cached && cached.players.length > 0) {
            setPlayers(cached.players);
            setSkeletonCards(0);
            setCurrentPage(cached.next);
        } else {
            setPlayers([]);
            setSkeletonCards(5);
            setCurrentPage(firstPage);
        }
    }, [locationId, query, compactTable]);

    const getPlayers = async () => {
        const requestId = requestIdRef.current;
        setLoader(true);
        try {
            const response = await api.get(currentPage);
            if (requestId !== requestIdRef.current) return;

            const results = response.data.results || [];
            const totalCount = response.data.count || 0;
            const next = response.data.next ? response.data.next.replace(/^https?:\/\/[^/]+/, "") : null;

            setPlayers(prev => {
                const existingIds = new Set(prev.map(p => p.id));
                const updated = [...prev, ...results.filter(p => !existingIds.has(p.id))];
                if (query.trim().length < 2) locationPlayersCache[locationId] = { players: updated, next };
                return updated;
            });
            setCurrentPage(next);

            const updatedCount = players.length + results.length;
            const remaining = totalCount - updatedCount;
            setSkeletonCards(remaining > 0 ? Math.min(5, remaining) : 0);
        } catch (error) {
            if (requestId !== requestIdRef.current) return;
            console.error(error);
            setFailed(true);
            setCurrentPage(null);
            setSkeletonCards(0);
        } finally {
            if (requestId === requestIdRef.current) setLoader(false);
        }
    };

    useEffect(() => {
        if ((inView || players.length === 0) && currentPage && !loader) {
            getPlayers();
        }
    }, [inView, currentPage, loader]);

    const handleItemClick = (player) => {
        const params = new URLSearchParams(routerLocation.search);
        params.set("player_id", player.id);
        history.push({ search: params.toString() });
    };

    const isSearching = query.trim().length >= 2;
    const showSkeletons = (loader || (currentPage && players.length === 0)) && skeletonCards > 0;

    return (
        <div>
            <div style={{ margin: "10px 0" }}>
                <div className="form-group" style={{ position: "relative" }}>
                    <FeatherIcon icon="search" size={16} className="search-icon" />
                    <input
                        ref={searchInputRef}
                        type="text"
                        placeholder={t("Search player by name")}
                        style={{ paddingLeft: "30px" }}
                        value={searchQuery}
                        onChange={(e) => { setSearchQuery(e.target.value); debouncedSetQuery(e.target.value); }}
                        className="search"
                    />
                    {searchQuery && (
                        <FeatherIcon
                            icon="x"
                            size={16}
                            onClick={() => { setSearchQuery(""); debouncedSetQuery.cancel(); setQuery(""); }}
                            style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", color: "#888", cursor: "pointer" }}
                        />
                    )}
                </div>
            </div>

            {failed && players.length === 0 ? (
                <p style={{ padding: "15px", textAlign: "center", color: "var(--secondary-color)" }}>{t("Something went wrong!")}</p>
            ) : !loader && players.length === 0 && currentPage === null ? (
                <p style={{ padding: "15px", textAlign: "center", color: "var(--secondary-color)" }}>{isSearching ? t("No players found") : t("No players")}</p>
            ) : compactTable ? (
                <div className="trows">
                    {players.map((player, idx) => (
                        <CompactTableRow
                            key={player.id || idx}
                            variant="entity"
                            zebra={idx % 2 === 1}
                            avatarSrc={player.image}
                            title={`${player.last_name} ${player.first_name}`.trim()}
                            subtitle={[
                                player.city,
                                player.country && t(getOptionName(COUNTRY_OPTIONS, player.country)),
                                player.level && t(getOptionName(LEVEL_OPTIONS, player.level))
                            ].filter(Boolean).join(", ")}
                            onClick={() => handleItemClick(player)}
                        />
                    ))}
                    {showSkeletons && Array.from({ length: skeletonCards }).map((_, idx) => (
                        <div className="trow trow-entity" key={`skel-player-${idx}`} style={{ pointerEvents: "none" }}>
                            <Skeleton circle width={20} height={20} />
                            <Skeleton width="60%" height={12} />
                            <span />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="entity-cards">
                    {players.map((player, idx) => (
                        <div className="entity-card" key={player.id || idx} onClick={() => handleItemClick(player)}>
                            <div className="entity-card-top">
                                <div className="entity-card-thumb">
                                    {player.image
                                        ? <img src={player.image} alt="" />
                                        : <FeatherIcon icon="user" />}
                                </div>
                                <div className="entity-card-titles">
                                    <div className="entity-card-title"><span className="uppercase">{player.last_name}</span> {player.first_name}</div>
                                    <div className="entity-card-subtitle">
                                        {player.city}
                                        {player.city && player.country && ", "}
                                        {t(getOptionName(COUNTRY_OPTIONS, player.country))}
                                        {(player.city || player.country) && player.level && ", "}
                                        {player.level && t(getOptionName(LEVEL_OPTIONS, player.level))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    {showSkeletons && Array.from({ length: skeletonCards }).map((_, idx) => (
                        <Skeleton key={`skel-player-${idx}`} height={82} baseColor="#fff" highlightColor="#f3f3f3" className="entity-card-skeleton" />
                    ))}
                </div>
            )}
            <div ref={ref} style={{ height: "20px", width: "100%" }}></div>
        </div>
    );
};

// Trimmed-down copy of ClubProfileModal.js (hero-collapse mechanics, tab
// switch scroll-pinning) - see that file for the full story behind each of
// those. Locations only get Calendar + Players (no Ratings tab - ratings are
// a club concept, not a venue one).
const LocationProfileModal = () => {
    const { t } = useTranslation();
    const api = useAxios();
    const history = useHistory();
    const routerLocation = useLocation();

    const query = new URLSearchParams(routerLocation.search);
    const locationId = query.get("location_id");

    const [loc, setLoc] = useState(() => (locationId ? locationCache[locationId] || null : null));
    const [loading, setLoading] = useState(() => (locationId ? !locationCache[locationId] : false));
    const [activeTab, setActiveTab] = useState("calendar");
    const [isShareOpen, setIsShareOpen] = useState(false);
    const [lightboxSrc, setLightboxSrc] = useState(null);
    const collapseRangeRef = useRef(90);
    const heroRef = useRef(null);
    const [progress, setProgress] = useState(0);

    useLayoutEffect(() => {
        let cancelled = false;
        let setupTimerId = null;
        let pinTimerId = null;
        let attachedTargets = [];
        let onScroll = null;

        const trySetup = () => {
            if (cancelled) return;
            const hero = heroRef.current;
            const overlay = hero?.closest(".modal-overlay");
            const content = hero?.closest(".modal-content");
            const targets = [overlay, content].filter(Boolean);
            if (!hero || targets.length === 0) {
                setupTimerId = setTimeout(trySetup, 16);
                return;
            }
            collapseRangeRef.current = hero.offsetHeight || collapseRangeRef.current;

            const waitUntilTall = () => {
                if (cancelled) return;
                const tallEnough = targets.some(t => t.scrollHeight - t.clientHeight >= collapseRangeRef.current - 1);
                if (!tallEnough) {
                    pinTimerId = setTimeout(waitUntilTall, 16);
                    return;
                }
                let tick = 0;
                const PIN_TICKS = 90;
                const reassert = () => {
                    if (cancelled) return;
                    targets.forEach(t => { t.scrollTop = 0; });
                    tick += 1;
                    if (tick < PIN_TICKS) pinTimerId = setTimeout(reassert, 16);
                };
                reassert();
            };
            waitUntilTall();

            onScroll = () => {
                const scrollTop = Math.max(overlay?.scrollTop || 0, content?.scrollTop || 0);
                setProgress(Math.min(1, scrollTop / collapseRangeRef.current));
            };
            targets.forEach(t => t.addEventListener("scroll", onScroll, { passive: true }));
            onScroll();
            attachedTargets = targets;
        };
        trySetup();

        return () => {
            cancelled = true;
            if (setupTimerId) clearTimeout(setupTimerId);
            if (pinTimerId) clearTimeout(pinTimerId);
            if (onScroll) attachedTargets.forEach(t => t.removeEventListener("scroll", onScroll));
        };
    }, [locationId]);

    const preSwitchScrollRef = useRef(null);
    const tabEffectMountedRef = useRef(false);
    const captureScrollBeforeSwitch = () => {
        const hero = heroRef.current;
        const overlay = hero?.closest(".modal-overlay");
        const content = hero?.closest(".modal-content");
        const raw = Math.max(overlay?.scrollTop || 0, content?.scrollTop || 0);
        preSwitchScrollRef.current = Math.min(raw, collapseRangeRef.current);
    };

    useLayoutEffect(() => {
        if (!tabEffectMountedRef.current) {
            tabEffectMountedRef.current = true;
            return;
        }
        const target = preSwitchScrollRef.current;
        if (target == null) return;
        const hero = heroRef.current;
        const overlay = hero?.closest(".modal-overlay");
        const content = hero?.closest(".modal-content");
        const targets = [overlay, content].filter(Boolean);
        if (!targets.length) return;

        let cancelled = false;
        let tick = 0;
        const PIN_TICKS = 90;
        const reassert = () => {
            if (cancelled) return;
            targets.forEach(t => { t.scrollTop = target; });
            tick += 1;
            if (tick < PIN_TICKS) setTimeout(reassert, 16);
        };
        reassert();
        return () => { cancelled = true; };
    }, [activeTab]);

    const tabSearchInputRef = useRef(null);
    const focusTabSearch = () => {
        tabSearchInputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        tabSearchInputRef.current?.focus();
    };

    useEffect(() => {
        if (!locationId) {
            setLoc(null);
            setLoading(false);
            return;
        }
        if (locationCache[locationId]) {
            setLoc(locationCache[locationId]);
            setLoading(false);
        } else {
            setLoading(true);
        }
        setActiveTab("calendar");
        api.get(`/api/v1.0/locations/${locationId}/`)
            .then(res => {
                locationCache[locationId] = res.data;
                setLoc(res.data);
            })
            .catch(() => {
                toast.error(t("Location not found"));
                handleClose();
            })
            .finally(() => setLoading(false));
    }, [locationId]);

    const handleClose = () => {
        const newQuery = new URLSearchParams(routerLocation.search);
        newQuery.delete("location_id");
        history.replace({ search: newQuery.toString() });
    };

    if (!locationId || (!loading && !loc)) return null;

    const addressLine = [loc?.city, loc?.country_name].filter(Boolean).join(", ");
    const heroAvatarOpacity = Math.max(0, 1 - progress * 2);
    const compactRowOpacity = Math.max(0, (progress - 0.5) * 2);

    return (
        <>
        <CustomModal isOpen={!!locationId} onClose={handleClose} hideCloseButton>
            <form className="modal-form">
                <div ref={heroRef} className="profile-hero">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", paddingTop: "10px", marginBottom: "10px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 500, fontSize: "100%" }}>
                            <FeatherIcon icon="map-pin" size={18} />
                            {t("Location")}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                            <FeatherIcon icon="share-2" size={20} className="profile-header-icon-btn" onClick={() => setIsShareOpen(true)} />
                            <FeatherIcon icon="x" size={20} className="profile-header-icon-btn" onClick={handleClose} />
                        </div>
                    </div>
                    <table style={{ width: "100%" }}>
                        <tbody>
                        <tr>
                            <td style={{ width: "110px", verticalAlign: "middle" }}>
                                {loc ? (
                                    (loc.image || loc.thumbnail)
                                        ? <img
                                              src={loc.image || loc.thumbnail}
                                              className="thumbnail big avatar-zoomable"
                                              alt={loc.name}
                                              style={{ transform: `scale(${1 - progress * 0.35})`, opacity: heroAvatarOpacity }}
                                              onClick={() => setLightboxSrc(loc.image || loc.thumbnail)}
                                          />
                                        : <div className="no-thumbnail big initials" style={{ transform: `scale(${1 - progress * 0.35})`, opacity: heroAvatarOpacity }}>{getInitials(loc.name)}</div>
                                ) : (
                                    <div style={{ display: "flex" }}>
                                        <Skeleton circle width={100} height={100} />
                                    </div>
                                )}
                            </td>
                            <td style={{ verticalAlign: "top" }}>
                                <table className="player_modal" style={{ width: "100%" }}>
                                    <tbody>
                                    <tr>
                                        <td><label>{t("Name")}</label></td>
                                        <td>{loc ? loc.name : <Skeleton width="90%" />}</td>
                                    </tr>
                                    <tr>
                                        <td><label>{t("city")}</label></td>
                                        <td>{loc ? (addressLine || "—") : <Skeleton width="70%" />}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ border: 0 }}><label>{t("address")}</label></td>
                                        <td style={{ border: 0 }}>{loc ? (loc.address || "—") : <Skeleton width="60%" />}</td>
                                    </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>

                <div className="modal-sticky-header">
                    <div
                        className="profile-header-compact"
                        style={{
                            opacity: compactRowOpacity,
                            transform: `translateY(${(1 - compactRowOpacity) * 4}px)`,
                            pointerEvents: progress > 0.5 ? "auto" : "none",
                            transition: "none"
                        }}
                    >
                        <span className="profile-header-compact-avatar">
                            {loc ? (
                                (loc.image || loc.thumbnail)
                                    ? <img src={loc.image || loc.thumbnail} alt="" className="avatar-zoomable" onClick={() => setLightboxSrc(loc.image || loc.thumbnail)} />
                                    : <span className="no-thumbnail initials">{getInitials(loc.name)}</span>
                            ) : (
                                <Skeleton circle width={50} height={50} />
                            )}
                        </span>
                        <span className="profile-header-compact-titles">
                            <span className="profile-header-compact-name">{loc ? loc.name : <Skeleton width={90} />}</span>
                            <span className="profile-header-compact-subtitle">{loc ? addressLine : <Skeleton width={60} />}</span>
                        </span>
                        <span className="profile-header-compact-share" style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                            {(activeTab === "calendar" || activeTab === "players") && (
                                <FeatherIcon icon="search" size={20} className="profile-header-icon-btn" onClick={focusTabSearch} />
                            )}
                            <FeatherIcon icon="share-2" size={20} className="profile-header-icon-btn" onClick={() => setIsShareOpen(true)} />
                            <FeatherIcon icon="x" size={20} className="profile-header-icon-btn" onClick={handleClose} />
                        </span>
                    </div>

                    <ul className="tournament-header-tabs" style={{ marginTop: "10px" }}>
                        <li className={activeTab === "calendar" ? "active" : ""}>
                            <a href="#" onMouseDown={e => e.preventDefault()} onClick={e => { e.preventDefault(); captureScrollBeforeSwitch(); setActiveTab("calendar"); }}>{t("Calendar")}</a>
                        </li>
                        <li className={activeTab === "players" ? "active" : ""}>
                            <a href="#" onMouseDown={e => e.preventDefault()} onClick={e => { e.preventDefault(); captureScrollBeforeSwitch(); setActiveTab("players"); }}>{t("Players")}</a>
                        </li>
                    </ul>
                </div>
                <div style={{ minHeight: "100vh" }}>
                    {loc && activeTab === "calendar" && <LocationCalendarTab locationId={locationId} searchInputRef={tabSearchInputRef} />}
                    {loc && activeTab === "players" && <LocationPlayersTab locationId={locationId} searchInputRef={tabSearchInputRef} />}
                </div>
            </form>
        </CustomModal>
        <TournamentShareModal
            isOpen={isShareOpen}
            onClose={() => setIsShareOpen(false)}
            url={window.location.href}
            title={loc?.name}
            shareText={loc?.name ? t("Check out the location {{name}}!", { name: loc.name }) : undefined}
            linkLabel={t("Location link")}
            qrCaption={t("Scan with camera to open the location")}
        />
        <PhotoLightbox src={lightboxSrc} alt={loc?.name} onClose={() => setLightboxSrc(null)} />
        </>
    );
};

export default LocationProfileModal;
